import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Table,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
// import authApi from "services/authApi";
import http from "services/httpClient";

interface TriggerEntry {
  threshold: number;
  operator: string;
  trigger_count: number;
  reset_mode: number;
  id?: number;
}

const EMPTY_TRIGGER = (): TriggerEntry => ({
  threshold: 0,
  operator: ">",
  trigger_count: 1,
  reset_mode: 0,
});

interface CalculatedParam {
  id: number;
  device_id: number;
  column_index: number;
  expression: string;
  result_label: string;
  unit: string | null;
}

interface EditCalculatedParamsFormProps {
  deviceId: number;
}

const EditCalculatedParamsForm: React.FC<EditCalculatedParamsFormProps> = ({ deviceId }) => {
  const [params, setParams] = useState<CalculatedParam[]>([]);
  const [expression, setExpression] = useState("");
  const [resultLabel, setResultLabel] = useState("");
  const [unit, setUnit] = useState("");
  const [nextColumnIndex, setNextColumnIndex] = useState<number>(100);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editExpr, setEditExpr] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [editUnit, setEditUnit] = useState("");
  // const [triggers, setTriggers] = useState<Record<number, Trigger | null>>({});
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [selectedParam, setSelectedParam] = useState<CalculatedParam | null>(null);
  const [triggerData, setTriggerData] = useState<TriggerEntry | null>(null);
  const handleOpenTrigger = async (param: CalculatedParam) => {
    try {
      const res = await http.get(
        `/triggers/?device_id=${deviceId}&column_index=${param.column_index}`
      );

      // /triggers/ returns an array.
      // Calculated parameters currently use the first trigger.
      const trigger = Array.isArray(res.data) ? res.data[0] : res.data;

      if (trigger) {
        setTriggerData({
          id: trigger.id,
          threshold: Number(trigger.threshold ?? 0),
          operator: trigger.operator ?? ">",
          trigger_count: Number(trigger.trigger_count ?? 1),
          reset_mode: Number(trigger.reset_mode ?? 0),
        });
      } else {
        setTriggerData(EMPTY_TRIGGER());
      }
    } catch (err: any) {
      console.error("Failed to fetch trigger", err);
      setTriggerData(EMPTY_TRIGGER());
    }

    setSelectedParam(param);
    setShowTriggerModal(true);
  };

  const handleSaveTrigger = async () => {
    if (!selectedParam || !triggerData) return;

    const payload = {
      device_id: deviceId,
      column_index: selectedParam.column_index,
      threshold: Number(triggerData.threshold ?? 0),
      operator: triggerData.operator,
      trigger_count: Math.max(
        1,
        Number(triggerData.trigger_count ?? 1)
      ),
      reset_mode: Number(triggerData.reset_mode ?? 0) === 1 ? 1 : 0,
    };

    try {
      if (triggerData.id) {
        await http.patch(`/triggers/${triggerData.id}`, payload);
      } else {
        await http.post(`/triggers`, payload);
      }

      setShowTriggerModal(false);
      fetchParams();
    } catch (err: any) {
      console.error("Trigger save failed", err);
    }
  };



  const startEdit = (param: CalculatedParam) => {
    setEditingId(param.id);
    setEditExpr(param.expression);
    setEditLabel(param.result_label);
    setEditUnit(param.unit || "");
  };

  const fetchParams = async () => {
    try {
      const res = await http.get(`/calculated_params/${deviceId}`);
      setParams(res.data);
      const max = res.data.reduce((acc: number, cur: CalculatedParam) =>
        Math.max(acc, cur.column_index), 99);
      setNextColumnIndex(max + 1);
    } catch (err) {
      // console.error("Error fetching calculated params", err);
    }
  };

  const handleAdd = async () => {
      if (!expression || !resultLabel) {
          alert("Expression and label are required.");
          return;
      }

      try {
          const payload = {
          column_index: nextColumnIndex,
          expression, // <== no translation now
          result_label: resultLabel,
          unit,
          };

          const res = await http.post(`/calculated_params/${deviceId}`, payload);
          setParams([...params, res.data]);

          setExpression("");
          setResultLabel("");
          setUnit("");
          setNextColumnIndex(nextColumnIndex + 1);
      } catch (err: any) {
          alert("Failed to add calculated param: " + err.response?.data?.detail || err.message);
      }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await http.put(`/calculated_params/${editingId}`, {
        expression: editExpr,
        result_label: editLabel,
        unit: editUnit,
      });
      setEditingId(null);
      fetchParams(); // reload list
    } catch (err: any) {
      alert("Failed to update param: " + err.response?.data?.detail || err.message);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditExpr("");
    setEditLabel("");
    setEditUnit("");
  };

  const handleDelete = async (paramId: number) => {
    if (!window.confirm("Delete this calculated parameter?")) return;
    try {
      await http.delete(`/calculated_params/${paramId}`);
      setParams(params.filter((p) => p.id !== paramId));
    } catch (err) {
      alert("Failed to delete parameter.");
      // console.error(err);
    }
  };

  useEffect(() => {
    if (deviceId) fetchParams();
  }, [deviceId]);

  return (
    <div>
      <h5>Add New Calculated Param</h5>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Expression</Form.Label>
            <Form.Control
                type="text"
                placeholder="e.g., (x0 + x1) / 2"
                value={expression}
                onChange={(e) => setExpression(e.target.value)}
                />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Label</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Avg Temp"
              value={resultLabel}
              onChange={(e) => setResultLabel(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Unit</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., °C"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2} className="d-flex align-items-end">
          <Button variant="success" onClick={handleAdd}>
            Add
          </Button>
        </Col>
      </Row>

      <hr />

      <h5>Existing Calculated Parameters</h5>
      <Table bordered striped>
        <thead>
          <tr>
            <th>Expression</th>
            <th>Label</th>
            <th>Unit</th>
            <th>Actions</th>
            <th>Trigger</th>
          </tr>
        </thead>
        <tbody>
          {params.map((param) => (
            <tr key={param.id}>
              <td>
                {editingId === param.id ? (
                  <Form.Control
                    value={editExpr}
                    onChange={(e) => setEditExpr(e.target.value)}
                  />
                ) : (
                  param.expression
                )}
              </td>
              <td>
                {editingId === param.id ? (
                  <Form.Control
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                  />
                ) : (
                  param.result_label
                )}
              </td>
              <td>
                {editingId === param.id ? (
                  <Form.Control
                    value={editUnit}
                    onChange={(e) => setEditUnit(e.target.value)}
                  />
                ) : (
                  param.unit || "-"
                )}
              </td>
              <td>
                {editingId === param.id ? (
                  <>
                    <Button size="sm" variant="success" onClick={saveEdit}>
                      Save
                    </Button>{" "}
                    <Button size="sm" variant="secondary" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="warning" onClick={() => startEdit(param)}>
                      Edit
                    </Button>{" "}
                    <Button size="sm" variant="danger" onClick={() => handleDelete(param.id)}>
                      Delete
                    </Button>
                  </>
                )}
              </td>
              <td className="text-center">
  <Button
    size="sm"
    variant="outline-secondary"
    onClick={() => handleOpenTrigger(param)}
  >
    Trig
  </Button>
</td>

            </tr>
          ))}
          {params.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center">
                No calculated parameters found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Modal show={showTriggerModal} onHide={() => setShowTriggerModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Set Trigger for {selectedParam?.result_label}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Operator</Form.Label>

            <Form.Select
              value={triggerData?.operator ?? ">"}
              onChange={(e) =>
                setTriggerData((prev) =>
                  prev
                    ? {
                        ...prev,
                        operator: e.target.value,
                      }
                    : null
                )
              }
            >
              <option value=">">&gt;</option>
              <option value="<">&lt;</option>
              <option value=">=">&ge;</option>
              <option value="<=">&le;</option>
              <option value="=">=</option>
              <option value="!=">≠</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Threshold</Form.Label>

            <Form.Control
              type="number"
              step="any"
              value={triggerData?.threshold ?? 0}
              onChange={(e) =>
                setTriggerData((prev) =>
                  prev
                    ? {
                        ...prev,
                        threshold: parseFloat(e.target.value) || 0,
                      }
                    : null
                )
              }
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Consecutive Readings</Form.Label>

                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  value={triggerData?.trigger_count ?? 1}
                  onChange={(e) =>
                    setTriggerData((prev) =>
                      prev
                        ? {
                            ...prev,
                            trigger_count: Math.max(
                              1,
                              parseInt(e.target.value, 10) || 1
                            ),
                          }
                        : null
                    )
                  }
                />

                <Form.Text className="text-muted">
                  Number of consecutive readings required.
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Reset Mode</Form.Label>

                <Form.Select
                  value={triggerData?.reset_mode ?? 0}
                  onChange={(e) =>
                    setTriggerData((prev) =>
                      prev
                        ? {
                            ...prev,
                            reset_mode: Number(e.target.value),
                          }
                        : null
                    )
                  }
                >
                  <option value={0}>Auto Reset</option>
                  <option value={1}>Manual Reset</option>
                </Form.Select>

                <Form.Text className="text-muted">
                  {triggerData?.reset_mode === 0
                    ? "Resets when the value returns to normal."
                    : "Allows another trigger sequence while abnormal."}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowTriggerModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSaveTrigger}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
    
  );
};

export default EditCalculatedParamsForm;
